import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState,
} from 'react';
import VehicleTypeService, {
    findNewestVehicle,
    VehicleType,
} from '@services/vehicle-type-service/vehicle-type-service';
import {
    ProfileWithVehiclesResponse,
    VehicleDetail,
} from '@models/profile-with-vehicles';
import AppConfigurationService from '@services/app-configuration-service/app-configuration-service';
import SecondaryNav from '@sections/secondary-nav/secondary-nav';
import useSecondaryNavContent from '@sections/secondary-nav/hook/use-secondary-nav-content';
import AuthenticationService from '@services/authentication-service/authentication-service';
import { useLocation } from 'react-router-dom';
import { setSessionStorageWithSelectedVIN } from '@/constants';

export interface ConnectedStatusContextProps {
    isConnected: boolean;
    hasVehicles: boolean;
    vin: string;
    firstName: string;
    setConnectedStatusContext?: (updates: ConnectedStatusContextProps) => void;
    getConnectedStatus?: (
        profile: ProfileWithVehiclesResponse,
        triggerVehicleTypeAPI: boolean
    ) => void;
}

export const ConnectedStatusContext = createContext({
    isConnected: false,
    hasVehicles: false,
    vin: null,
    firstName: null,
    setConnectedStatusContext: () => null,
    getConnectedStatus: () => null,
} as any);

export const ConnectedStatusProvider = ({ children }) => {
    const [connectedStatusContext, setConnectedVehicleStatusContext] =
        useState<ConnectedStatusContextProps | null>({
            isConnected: false,
            hasVehicles: false,
            vin: null,
            firstName: null,
        });
    const location = useLocation();
    const authenticationService = new AuthenticationService();
    const appConfig = new AppConfigurationService();
    const secondaryNavContent = useSecondaryNavContent();
    const isWebview =
        location.search?.includes('webview') ||
        location.pathname?.includes('signin-credentials');
    const [showSecondaryNav, setShowSecondaryNav] = useState<boolean>(false);

    useEffect(() => {
        authenticationService.onIsAuthenticated().then((isAuthenticated) => {
            if (
                isAuthenticated &&
                secondaryNavContent &&
                appConfig.currentLanguageRegionCode === 'en-us' &&
                appConfig.brand === 'ford' &&
                !isWebview
            ) {
                setShowSecondaryNav(true);
            } else if (isWebview) {
                setShowSecondaryNav(false);
            }
        });
    }, [secondaryNavContent, location, isWebview]);

    const setConnectedStatusContext = (
        connectedStatusContextUpdates: ConnectedStatusContextProps
    ) => {
        setConnectedVehicleStatusContext((prevState) => {
            return {
                ...prevState,
                ...connectedStatusContextUpdates,
            };
        });
    };

    const getConnectedStatus = (
        profile: ProfileWithVehiclesResponse,
        triggerVehicleTypeAPI: boolean
    ): void => {
        if (
            appConfig.currentLanguageRegionCode !== 'en-us' ||
            appConfig.brand === 'lincoln'
        ) {
            return;
        }
        if (profile?.vehicles?.length > 0) {
            setSessionStorageWithSelectedVIN(profile?.vehicles[0].vin);
            if (triggerVehicleTypeAPI) {
                const vehicle = findNewestVehicle(profile?.vehicles);
                const vehicleTypeService = new VehicleTypeService();
                vehicleTypeService
                    .requestVehicleType(
                        vehicle.vin,
                        appConfig.currentLanguageRegionCode,
                        appConfig.current3LetterCountryCapitalizedCode
                    )
                    .then((vehicleType: VehicleType) => {
                        setConnectedStatusContext({
                            isConnected: vehicleType.isConnected ?? false,
                            hasVehicles: true,
                            vin: profile?.vehicles[0]?.vin,
                            firstName: profile.profile.firstName,
                        });
                    })
                    .catch(() => {
                        setConnectedStatusContext({
                            isConnected: false,
                            hasVehicles: false,
                            vin: null,
                            firstName: profile.profile.firstName,
                        });
                    });
            } else {
                const connectVehicle: Array<VehicleDetail> =
                    profile?.vehicles?.filter((e) => {
                        return e.isConnected == true;
                    });
                setConnectedStatusContext({
                    isConnected:
                        connectVehicle.length > 0
                            ? connectVehicle[0]?.isConnected
                            : false,
                    hasVehicles: true,
                    vin: profile?.vehicles[0]?.vin,
                    firstName: profile.profile.firstName,
                });
            }
        } else {
            setConnectedStatusContext({
                isConnected: false,
                hasVehicles: false,
                vin: null,
                firstName: profile.profile.firstName,
            });
        }
    };

    const getContext = useCallback(() => {
        return {
            connectedStatusContext,
            setConnectedStatusContext,
            getConnectedStatus,
        };
    }, [connectedStatusContext]);

    return (
        <ConnectedStatusContext.Provider value={getContext()}>
            {showSecondaryNav && (
                <SecondaryNav locationPathname={location.pathname} />
            )}
            {children}
        </ConnectedStatusContext.Provider>
    );
};

export const useConnectedStatusContext = () =>
    useContext(ConnectedStatusContext);

export default ConnectedStatusProvider;
